import React from "react";
import { GridNode, FuelType, HoverElement } from "../../types";
import { FUEL_TYPES, getFuelIcon, getFuelLabel } from "../../utils/fuelTypes";
import KeyIcon from "./KeyIcon";
import ColumnHeader from "./ColumnHeader";

interface Props {
  generators: GridNode[];
  onNodeCostChange?: (nodeId: string, cost: number) => void;
  onNodeFuelTypeChange?: (nodeId: string, fuelType: FuelType) => void;
  onCommitEdit?: () => void;
  hoverElement: HoverElement | null;
  onElementHover: (element: HoverElement | null) => void;
  selectedBusId?: string | null;
  onBusSelect?: (nodeId: string) => void;
}

function GeneratorTable({ generators, onNodeCostChange, onNodeFuelTypeChange, onCommitEdit, hoverElement, onElementHover, selectedBusId, onBusSelect }: Props) {
  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse" as const,
    fontSize: "12px",
    marginTop: "10px",
  };

  const thStyle = {
    background: "#f5f5f5",
    padding: "8px",
    textAlign: "left" as const,
    borderBottom: "1px solid #ddd",
    fontWeight: "bold" as const,
  };

  const tdStyle = {
    padding: "6px 8px",
    borderBottom: "1px solid #eee",
  };

  if (generators.length === 0) {
    return (
      <p style={{ padding: "20px", textAlign: "center", color: "#666" }}>
        No generators in the grid
      </p>
    );
  }

  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          <th style={thStyle} title="Show LMP curve">LMP</th>
          <ColumnHeader column="id" style={thStyle}>ID<KeyIcon /></ColumnHeader>
          <ColumnHeader column="fuel" style={thStyle}>Fuel Type</ColumnHeader>
          <th style={thStyle}>Generation (MW)</th>
          <ColumnHeader column="cost" style={thStyle}>Cost ($/MWh)</ColumnHeader>
          <th style={thStyle}>Voltage (pu)</th>
          <th style={thStyle}>Position</th>
        </tr>
      </thead>
      <tbody>
        {generators.map((gen) => {
          const isHighlighted = hoverElement?.type === 'Node' && hoverElement?.id === gen.id;
          return (
          <tr 
            key={gen.id}
            onMouseEnter={() => onElementHover({ type: 'Node', id: gen.id })}
            onMouseLeave={() => onElementHover(null)}
            style={{ backgroundColor: isHighlighted ? '#FFD70030' : 'transparent' }}
          >
            <td style={{ ...tdStyle, textAlign: 'center' }}>
              <input
                type="radio"
                name="lmp-bus-select"
                checked={selectedBusId === gen.id}
                onChange={() => onBusSelect?.(gen.id)}
                onClick={(e) => e.stopPropagation()}
                style={{ cursor: 'pointer' }}
                title="Show LMP curve for this bus"
              />
            </td>
            <td style={tdStyle}>{gen.id}</td>
            <td style={tdStyle}>
              {onNodeFuelTypeChange ? (
                <select
                  value={gen.fuelType || 'fossil'}
                  onChange={(e) =>
                    onNodeFuelTypeChange(gen.id, e.target.value as FuelType)
                  }
                  style={{
                    width: "90px",
                    padding: "2px 4px",
                    border: "1px solid #ddd",
                    borderRadius: "3px",
                    fontSize: "11px",
                  }}
                >
                  {FUEL_TYPES.map((fuelType) => (
                    <option key={fuelType} value={fuelType}>
                      {getFuelIcon(fuelType)} {getFuelLabel(fuelType)}
                    </option>
                  ))}
                </select>
              ) : (
                <span style={{ fontSize: "11px" }}>
                  {gen.fuelType || "fossil"}
                </span>
              )}
            </td>
            <td style={tdStyle}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <input
                  type="range"
                  min={gen.minPowerMW}
                  max={gen.maxPowerMW}
                  value={gen.value}
                  disabled={true}
                  style={{
                    width: "80px",
                    height: "4px",
                    borderRadius: "2px",
                    background: "#f0f0f0",
                    outline: "none",
                    cursor: "not-allowed",
                    opacity: 0.6,
                  }}
                />
                <span style={{ minWidth: "45px", fontSize: "11px" }}>
                  {gen.value.toFixed(1)}
                </span>
              </div>
            </td>
            <td style={tdStyle}>
              {onNodeCostChange ? (
                <div
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    step="1"
                    value={gen.costPerMWh}
                    onChange={(e) =>
                      onNodeCostChange(gen.id, parseFloat(e.target.value) || 50)
                    }
                    onBlur={onCommitEdit}
                    style={{
                      width: "60px",
                      padding: "2px 4px",
                      border: "1px solid #ddd",
                      borderRadius: "3px",
                      fontSize: "11px",
                      textAlign: "center",
                    }}
                  />
                  <span style={{ fontSize: "10px", color: "#666" }}>$/MWh</span>
                </div>
              ) : (
                <span style={{ fontSize: "11px" }}>
                  {gen.costPerMWh?.toFixed(0) || "50"} $/MWh
                </span>
              )}
            </td>
            <td style={tdStyle}>{gen.voltage?.toFixed(3) || "N/A"}</td>
            <td style={tdStyle}>
              ({gen.x.toFixed(0)}, {gen.y.toFixed(0)})
            </td>
          </tr>
        );
        })}
      </tbody>
    </table>
  );
}

export default GeneratorTable;